import React, { useState } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  MenuItem,
  Popover,
  Button,
  Typography,
} from '@mui/material';
import { Clear, Close } from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { filterInputSx } from '../common/filterStyles';

export interface FilterValues {
  trained: string;
  certified: string;
  created_at: string;
  updated_at: string;
  document: string;
  field: string;
  training_revision: string;
  vendor_id: string;
  vendor_name: string;
}

export const emptyFilters: FilterValues = {
  trained: '',
  certified: '',
  created_at: '',
  updated_at: '',
  document: '',
  field: '',
  training_revision: '',
  vendor_id: '',
  vendor_name: '',
};

// Training Set ID is now in the main filter bar, not in the popover
// Training Revision is rendered separately next to Trained
const TEXT_FIELDS: { key: keyof FilterValues; placeholder: string }[] = [
  { key: 'document', placeholder: 'Document' },
  { key: 'field', placeholder: 'Field' },
  { key: 'vendor_id', placeholder: 'Vendor ID' },
  { key: 'vendor_name', placeholder: 'Vendor Name' },
];

interface TrainingSetFiltersPopoverProps {
  anchorEl: HTMLElement | null;
  currentValues: FilterValues;
  onClose: () => void;
  onApply: (values: FilterValues) => void;
}

const TrainingSetFiltersPopover = ({
  anchorEl,
  currentValues,
  onClose,
  onApply,
}: TrainingSetFiltersPopoverProps) => {
  const [draft, setDraft] = useState<FilterValues>(currentValues);
  const wasOpenRef = React.useRef(false);

  React.useEffect(() => {
    const isOpen = Boolean(anchorEl);
    if (isOpen && !wasOpenRef.current) {
      setDraft(currentValues);
    }
    wasOpenRef.current = isOpen;
  }, [anchorEl, currentValues]);

  const handleReset = () => setDraft(emptyFilters);

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{ paper: { sx: { mt: 0.5, borderRadius: '8px', p: 2, width: 420 } } }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onApply(draft);
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Filters
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          {/* Trained */}
          <TextField
            select
            size="small"
            label="Trained"
            variant="outlined"
            value={draft.trained || ''}
            onChange={(e) => setDraft((prev) => ({ ...prev, trained: e.target.value }))}
            data-cy="filter-trained"
            sx={filterInputSx}
          >
            <MenuItem value="">Any</MenuItem>
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </TextField>

          {/* Certified */}
          <TextField
            select
            size="small"
            label="Certified"
            variant="outlined"
            value={draft.certified || ''}
            onChange={(e) => setDraft((prev) => ({ ...prev, certified: e.target.value }))}
            data-cy="filter-certified"
            sx={filterInputSx}
          >
            <MenuItem value="">Any</MenuItem>
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </TextField>

          {/* Training Revision */}
          <TextField
            size="small"
            placeholder="Training Revision"
            label="Training Revision"
            variant="outlined"
            autoComplete="off"
            value={draft.training_revision}
            onChange={(e) => setDraft((prev) => ({ ...prev, training_revision: e.target.value }))}
            data-cy="filter-training_revision"
            sx={filterInputSx}
            InputProps={{
              endAdornment: draft.training_revision && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setDraft((prev) => ({ ...prev, training_revision: '' }))}
                    edge="end"
                    sx={{ mr: -1, p: 0.25 }}
                  >
                    <Clear sx={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Created Date */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Created Date"
              value={draft.created_at ? dayjs(draft.created_at) : null}
              onChange={(date) =>
                setDraft((prev) => ({
                  ...prev,
                  created_at: date && date.isValid() ? date.format('YYYY-MM-DD') : '',
                }))
              }
              slotProps={{
                textField: {
                  size: 'small',
                  variant: 'outlined',
                  sx: filterInputSx,
                  inputProps: { 'data-cy': 'filter-created-date' },
                },
                field: {
                  clearable: true,
                  onClear: () => setDraft((prev) => ({ ...prev, created_at: '' })),
                },
              }}
            />
          </LocalizationProvider>

          {/* Updated Date */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Updated Date"
              value={draft.updated_at ? dayjs(draft.updated_at) : null}
              onChange={(date) =>
                setDraft((prev) => ({
                  ...prev,
                  updated_at: date && date.isValid() ? date.format('YYYY-MM-DD') : '',
                }))
              }
              slotProps={{
                textField: {
                  size: 'small',
                  variant: 'outlined',
                  sx: filterInputSx,
                  inputProps: { 'data-cy': 'filter-updated-date' },
                },
                field: {
                  clearable: true,
                  onClear: () => setDraft((prev) => ({ ...prev, updated_at: '' })),
                },
              }}
            />
          </LocalizationProvider>

          {/* Text fields */}
          {TEXT_FIELDS.map(({ key, placeholder }) => (
            <TextField
              key={key}
              size="small"
              placeholder={placeholder}
              label={placeholder}
              variant="outlined"
              autoComplete="off"
              value={draft[key]}
              onChange={(e) => setDraft((prev) => ({ ...prev, [key]: e.target.value }))}
              data-cy={`filter-${key}`}
              sx={filterInputSx}
              InputProps={{
                endAdornment: draft[key] && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setDraft((prev) => ({ ...prev, [key]: '' }))}
                      edge="end"
                      sx={{ mr: -1, p: 0.25 }}
                    >
                      <Clear sx={{ fontSize: 14 }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          ))}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2.5 }}>
          <Button
            size="small"
            type="button"
            onClick={handleReset}
            data-cy="btn-reset-filters"
            sx={{
              textTransform: 'none',
              fontSize: '12.5px',
              fontWeight: 500,
              height: 34,
              borderRadius: '6px',
              color: '#5f6d7e',
              border: '1px solid rgba(0,0,0,0.12)',
              '&:hover': { borderColor: 'rgba(0,0,0,0.24)', backgroundColor: 'rgba(0,0,0,0.02)' },
            }}
          >
            Reset
          </Button>
          <Button
            size="small"
            type="submit"
            variant="contained"
            data-cy="btn-apply-filters"
            sx={{
              textTransform: 'none',
              fontSize: '12.5px',
              fontWeight: 500,
              height: 34,
              borderRadius: '6px',
              backgroundColor: '#1D9AAA',
              color: '#fff',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#178a99', boxShadow: 'none' },
            }}
          >
            Apply
          </Button>
        </Box>
      </form>
    </Popover>
  );
};

export default TrainingSetFiltersPopover;
